import { Button, Form, PageHeader } from 'antd'
import cloneDeep from 'lodash.clonedeep'
import { useEffect, useMemo, useState } from 'react'

import FormBlock from '../../components/forms/FormBlock'
import FormWrap from '../../components/forms/FormWrap'
import { formOptions } from '../../forms/blocks'
import {
  ProductsMediaForm,
  ProductsOptionsForm,
  ProductsPublicationForm,
} from '../../forms/Products'
import { createProduct } from '../../network/Products'
import { useParams } from 'react-router-dom'
import { getProductCategoriesBasic } from '../../network/API'
import { getProductTags } from '../../network/Tags'
import { useAuth } from '../../hooks/useAuth'
import { getCurrency } from '../../network/Stores'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'

export const Details = ({ form }) => {
  const options = useMemo(() => {
    const options = cloneDeep(formOptions)
    delete options.collection
    delete options.suit_2_pieces
    delete options.suit_3_pieces
    delete options.default
    return options
  }, [])

  const DetailItem = ({ _, fieldIndex, remove }) => {
    const [index, setIndex] = useState(-1)
    const [key, setKey] = useState(null)
    const [template, setTemplate] = useState(null)
    const [isInitialized, setIsInitialized] = useState(false)
    
    const detailValue = Form.useWatch(['details', fieldIndex], form)

    useEffect(() => {
      if (key && options[key]) {
        const tpl = cloneDeep(options[key].forms[0].form[0])
        for (let i in tpl.sections) {
          for (let j in tpl.sections[i].fields) {
            tpl.sections[i].fields[j].key.splice(0, 0, key)
            tpl.sections[i].fields[j].key.splice(0, 0, fieldIndex)
          }
        }

        setTemplate(tpl)
      }
    }, [fieldIndex, key])

    useEffect(() => {
      if (index >= 0) {
        setKey(Object.keys(options)[index])
      }
    }, [index])

    // Detect product type from form data
    useEffect(() => {
      const detail = form.getFieldValue(['details', fieldIndex])
      
      if (detail && Object.keys(detail).length > 0) {
        const detailKeys = Object.keys(detail).filter(k => k !== '_id')
                
        const foundIndex = Object.keys(options).findIndex(
          (key) => detailKeys.includes(key) && !!detail[key] && Object.keys(detail[key]).length > 0
        )
        
        if (foundIndex >= 0) {
          const foundKey = Object.keys(options)[foundIndex]
          setIndex(foundIndex)
          setKey(foundKey)
          setIsInitialized(true)
        } else if (!isInitialized) {
          setIndex(0)
          setKey('vest')
          setIsInitialized(true)
        }
      } else if (!isInitialized) {
        setIndex(0)
        setKey('vest')
        setIsInitialized(true)
      }
    }, [fieldIndex, detailValue, isInitialized])

    return (
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            width: 'fit-content',
            columnGap: 12,
            rowGap: 12,
          }}
        >
          {/* According to the selected options, render different forms */}
          {Object.keys(options).map((key, i) => (
            <div
              key={i}
              className={`order-btn big ${index === i ? 'selected' : ''}`}
              onClick={() => {
                form.setFieldValue(['details', fieldIndex], {})
                setIndex(i)
                setKey(Object.keys(options)[i])
              }}
            >
              {options[key].name}
            </div>
          ))}
          <Button
            type="ghost"
            onClick={() => remove(fieldIndex)}
            style={{
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 5,
              color: '#9e1313',
              border: '1px solid #d1abab',
              '&:hover': {
                background: '#d1abab',
              },
            }}
          >
            Delete
          </Button>
        </div>
        {template && (
          <FormBlock
            form={form}
            formTemplate={template || options[key].forms[0].form[0]}
          />
        )}
      </>
    )
  }

  return (
    <Form.List name="details">
      {(fields, { add, remove }) => (
        <div className="section-from-vertical">
          {fields.map((field, fieldIndex) => {
            return (
              <DetailItem
                key={fieldIndex}
                form={form}
                field={field}
                fieldIndex={fieldIndex}
                remove={remove}
              />
            )
          })}
          <Button
            type="primary"
            style={{
              maxWidth: 200,
            }}
            onClick={() => {
              add({
                vest: options.vest.forms[0].form[0],
              })
            }}
          >
            Add Category
          </Button>
        </div>
      )}
    </Form.List>
  )
}

const CreateMpProduct = ({ history }) => {
  const [form] = Form.useForm()
  const { storeId } = useParams()
  const { user } = useAuth()

  const [currencySymbol, setCurrencySymbol] = useState('¥')

  useEffect(() => {
    const fetchCurrency = async () => {
      const currencyValue = await getCurrency(storeId)
      const symbol = getCurrencySymbol(currencyValue.currency)
      setCurrencySymbol(symbol)
    }

    fetchCurrency()
  }, [storeId])

  const ProductsDescriptionForm = {
    title: 'Description',
    key: [],
    sections: [
      {
        columns: 2,
        fields: [
          {
            label: 'Product Name',
            placeholder: 'Name',
            isRequired: true,
            key: ['name', 'en'],
            pos: {
              col: 1,
              row: 1,
              size: 1,
            },
          },
          // Display only if the store is not a franchise
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Product Name (中文)',
                  placeholder: 'Chinese name',
                  isRequired: true,
                  key: ['name', 'zh'],
                  pos: {
                    col: 1,
                    row: 2,
                    size: 1,
                  },
                },
              ]
            : []),
          {
            label: 'Type',
            type: 'select',
            isRequired: true,
            key: ['productTypeFixed'],
            options: [
              { label: 'Fixed design', key: 'fixed_design' },
              { label: 'Fixed SKU', key: 'fixed_sku' },

            ],
            pos: {
              col: 1,
              row: 3,
              size: 1,
            },
          },
          {
            label: 'SKU',
            placeholder: 'Ref ID',
            isRequired: true,
            key: ['sku'],
            pos: {
              col: 1,
              row: 4,
              size: 1,
            },
          },
          {
            label: 'Collection',
            type: 'select',
            isRequired: true,
            key: ['category'],
            requestOption: async ({ queryKey }) => {
              const currentStore = user.currentStore
              const filter = { store: currentStore }

              return getProductCategoriesBasic({
                queryKey: [
                  'getProductCategoriesBasic',
                  {
                    filter,
                    sort: {},
                    range: {},
                    tabFilter: {},
                  },
                ],
              })
            },

            pos: {
              col: 1,
              row: 5,
              size: 1,
            },
          },
          {
            label: 'Tag',
            type: 'select',
            key: ['tag'],
            isRequired: false,
            requestOption: async ({ queryKey }) => {
              const currentStore = user.currentStore
              const filter = { store: currentStore }

              return getProductTags({
                queryKey: [
                  'getProductTags',
                  {
                    filter,
                    sort: {},
                    range: {},
                    tabFilter: {},
                  },
                ],
              })
            },
            pos: {
              col: 1,
              row: 6,
              size: 1,
            },
          },
          {
            label: `Retail price (${currencySymbol})`,
            isRequired: true,
            key: ['retailPrice'],
            pos: {
              col: 1,
              row: 7,
              size: 1,
            },
          },
          {
            label: 'Delivery time (days)',
            isRequired: true,
            key: ['deliveryTime'],
            pos: {
              col: 1,
              row: 8,
              size: 1,
            },
          },
          {
            label: 'Description',
            type: 'text',
            isRequired: true,
            key: ['description', 'en'],
            pos: {
              col: 1,
              row: 9,
              size: 1,
            },
          },
          // Display only if the store is not a franchise
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Description (中文)',
                  type: 'text',
                  isRequired: true,
                  key: ['description', 'zh'],
                  pos: {
                    col: 1,
                    row: 10,
                    size: 1,
                  },
                },
                {
                  label: 'Gender',
                  type: 'basicRadio',
                  isRequired: false,
                  key: ['gender'],
                  options: [
                    { label: 'Male', key: 'male' },
                    { label: 'Female', key: 'female' },
                    { label: 'Both', key: 'both' },
                  ],
                  pos: {
                    col: 1,
                    row: 11,
                    size: 1,
                  },
                },
                {
                  label: 'Reference',
                  isRequired: false,
                  key: ['reference'],
                  pos: {
                    col: 1,
                    row: 12,
                    size: 1,
                  },
                },
                {
                  label: 'Show for mix-and-match',
                  type: 'checkbox',
                  isRequired: false,
                  key: ['mixAndMatch'],
                  default: false,
                  pos: {
                    col: 1,
                    row: 13,
                    size: 1,
                  },
                },
              ]
            : []),
        ],
      },
    ],
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title={'Create product'}
        onBack={() => {
          const url = createUrlWithFiltersFromCurrentURL('/mp-products')
          history.push(url)
        }}
        style={{ backgroundColor: '#fff' }}
      />
      <FormWrap
        form={form}
        title="create_client"
        request={createProduct}
        cancelText={'Cancel'}
        okText={'Save'}
        onSuccess={() => {
          const url = createUrlWithFiltersFromCurrentURL('/mp-products')
          history.push(url)
        }}
        cleanBeforeSending={(oldData) => {
          oldData.store = storeId
          
          // Clean up null/undefined values from details
          if (oldData.details && Array.isArray(oldData.details)) {
            oldData.details = oldData.details.map(detail => {
              const cleanedDetail = {}
              
              if (detail._id) {
                cleanedDetail._id = detail._id
              }
              
              // For each product type (shoes, vest, pant, etc.)
              Object.keys(detail).forEach(productType => {
                if (productType === '_id') return
                
                if (detail[productType] && typeof detail[productType] === 'object') {
                  const cleanedProductData = {}
                  
                  // Remove null/undefined values from product data
                  Object.keys(detail[productType]).forEach(key => {
                    const value = detail[productType][key]
                    if (value !== null && value !== undefined && value !== '') {
                      cleanedProductData[key] = value
                    }
                  })
                  
                  // Special handling for shoes: TODO: Need to check for next version the requirement for each product
                  if (productType === 'shoes' && Object.keys(cleanedProductData).length > 0) {
                    const requiredDefaults = {
                      soleConfection: 'blake',
                      soleMaterial: 'leather',
                      sole: 'welted',
                      leatherSoleOption: 'plain',
                      topReinforcement: 'no',
                      frontTop: 'no',
                      fur: 'no',
                      heels: 'no'
                    }
                    
                    // Apply defaults only for fields that are completely missing
                    Object.keys(requiredDefaults).forEach(field => {
                      if (!cleanedProductData.hasOwnProperty(field)) {
                        cleanedProductData[field] = requiredDefaults[field]
                      }
                    })
                  }
                  
                  // Only include product type if it has data
                  if (Object.keys(cleanedProductData).length > 0) {
                    cleanedDetail[productType] = cleanedProductData
                  }
                }
              })
              
              return cleanedDetail
            })
          }
          
          if (oldData.mainPicture) {
            let main = oldData.mainPicture.fileList
              ? oldData.mainPicture?.fileList
              : oldData.mainPicture

            if (!Array.isArray(main)) {
              main = [main]
            }
            const newPic = main.map((file) =>
              file.response ? file.response : file
            )

            oldData.mainPicture = newPic.length ? newPic[0] : {}
          } else {
            oldData.mainPicture = {}
          }

          if (oldData.otherPictures) {
            const otherPic = oldData.otherPictures.fileList
              ? oldData.otherPictures?.fileList
              : oldData.otherPictures
            oldData.otherPictures = otherPic.map((file) =>
              file.response ? file.response : file
            )
          } else {
            oldData.otherPictures = []
          }

          return oldData
        }}
        // For debugging form data change
        onValuesChange={(changedValues, allValues) => {
          // console.log(allValues)
        }}
        invalidate={['getProducts']}
      >
        <div className="section-from-vertical">
          <FormBlock form={form} formTemplate={ProductsDescriptionForm} />
          <Details form={form} />
          {!user.franchiseStore && (
            <FormBlock form={form} formTemplate={ProductsMediaForm} />
          )}
          <FormBlock form={form} formTemplate={ProductsOptionsForm} />
          <FormBlock.Basic formTemplate={ProductsPublicationForm} />
        </div>
      </FormWrap>
    </>
  )
}

export default CreateMpProduct
